// frontend/src/askos/SettingsPage.js
//
// Student profile/settings — ported from pages/AskOS.js's Profile/Language/
// Voice/Theme/Accessibility tabs (missing from the new Samkalp dashboard).
// Photo/display name/language persist to the backend via the existing
// GET/PATCH /tenant/{slug}/askos/me (no backend changes needed). Theme/
// font/accessibility/voice prefs are browser-local only, same as the old
// implementation (StudentProfile has no columns for these).
//
// Scope note: the old file's password-change form is NOT ported — it PATCHes
// current_password/new_password to this same endpoint, but the currently
// active backend handler (app/routers/askos_router.py:373) only accepts
// StudentProfileUpdate's typed fields (no password fields at all), so that
// flow doesn't actually work server-side today. Not rebuilding a broken flow.
import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Alert, Spinner, Button, Row, Col, Form } from 'react-bootstrap';
import api, { askosAuthHeader } from './askosApi';
import {
    LANGUAGES, THEMES, ACCENT_PRESETS, FONT_SIZES, LINE_SPACINGS, VOICE_SPEEDS,
    loadPrefs, savePrefs, applyPreferences, speakText, photoKey,
} from './prefsUtils';

const SectionLabel = ({ children }) => (
    <p className="settings-section-label">{children}</p>
);

const OptionCard = ({ selected, onClick, children }) => (
    <button type="button" onClick={onClick} className={`settings-option-card${selected ? ' selected' : ''}`} aria-pressed={selected}>
        {selected && <span className="settings-option-check">✓</span>}
        {children}
    </button>
);

const ToggleRow = ({ id, label, description, checked, onChange }) => (
    <div className="settings-toggle-row">
        <div>
            <label htmlFor={id} className="settings-toggle-label">{label}</label>
            {description && <p className="settings-toggle-desc">{description}</p>}
        </div>
        <Form.Check type="switch" id={id} checked={checked} onChange={e => onChange(e.target.checked)} aria-label={label} />
    </div>
);

const PillGroup = ({ options, value, onChange, getLabel, getValue }) => (
    <div className="settings-pill-group">
        {options.map(opt => {
            const v = getValue(opt); const active = value === v;
            return (
                <button key={v} type="button" onClick={() => onChange(v)} className={`settings-pill${active ? ' active' : ''}`} aria-pressed={active}>
                    {getLabel(opt)}
                </button>
            );
        })}
    </div>
);

const VoiceModelSelector = ({ prefs, onChange }) => {
    const [voices, setVoices] = useState([]);
    useEffect(() => {
        const load = () => setVoices(window.speechSynthesis?.getVoices() || []);
        load();
        window.speechSynthesis?.addEventListener('voiceschanged', load);
        return () => window.speechSynthesis?.removeEventListener('voiceschanged', load);
    }, []);
    if (voices.length === 0) return null;
    const INDIAN_LANGS = new Set(['hi-IN', 'ta-IN', 'te-IN', 'ml-IN', 'kn-IN', 'bn-IN', 'mr-IN', 'en-IN']);
    const norm = v => v.lang.replace('_', '-');
    const indian = voices.filter(v => INDIAN_LANGS.has(norm(v)));
    const english = voices.filter(v => v.lang.startsWith('en') && !INDIAN_LANGS.has(norm(v)));
    const other = voices.filter(v => !v.lang.startsWith('en') && !INDIAN_LANGS.has(norm(v)));
    return (
        <div className="mb-4">
            <p className="fw-semibold small mb-2">Voice Model</p>
            <Form.Select size="sm" value={prefs.voiceName || ''} onChange={e => onChange({ ...prefs, voiceName: e.target.value || null })}>
                <option value="">Auto (recommended)</option>
                {indian.length > 0 && <optgroup label={`— Indian Voices (${indian.length}) —`}>{indian.map(v => <option key={v.voiceURI} value={v.name}>{v.name} [{v.lang}]</option>)}</optgroup>}
                {english.length > 0 && <optgroup label={`— English Voices (${english.length}) —`}>{english.map(v => <option key={v.voiceURI} value={v.name}>{v.name} [{v.lang}]</option>)}</optgroup>}
                {other.length > 0 && <optgroup label={`— Other (${other.length}) —`}>{other.map(v => <option key={v.voiceURI} value={v.name}>{v.name} [{v.lang}]</option>)}</optgroup>}
            </Form.Select>
        </div>
    );
};

const SettingsPage = ({ slug }) => {
    const [profile, setProfile] = useState(null);
    const [fullName, setFullName] = useState('');
    const [localPhoto, setLocalPhoto] = useState('');
    const [prefs, setPrefsState] = useState(loadPrefs);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        api.get(`/tenant/${slug}/askos/me`, { headers: askosAuthHeader() })
            .then(res => {
                setProfile(res.data);
                setFullName(res.data.full_name || '');
                setLocalPhoto(res.data.profile_photo || localStorage.getItem(photoKey(slug)) || '');
            })
            .catch(() => setMsg({ type: 'danger', text: 'Could not load your profile.' }));
    }, [slug]);

    const onPrefsChange = (newPrefs) => {
        setPrefsState(newPrefs);
        savePrefs(newPrefs);
        applyPreferences(newPrefs);
        // Only language round-trips to the backend (AskOS chat reads it server-side).
        if (newPrefs.language !== prefs.language) {
            api.patch(`/tenant/${slug}/askos/me`, { language_preference: newPrefs.language }, { headers: askosAuthHeader() }).catch(() => {});
        }
    };

    const handlePhotoUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { setMsg({ type: 'danger', text: 'Please select an image file.' }); return; }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const data = ev.target.result;
            if (data.length > 400_000) { setMsg({ type: 'danger', text: 'Photo too large. Use an image under 300 KB.' }); return; }
            localStorage.setItem(photoKey(slug), data);
            setLocalPhoto(data);
            api.patch(`/tenant/${slug}/askos/me`, { profile_photo: data }, { headers: askosAuthHeader() })
                .then(() => setMsg({ type: 'success', text: 'Profile photo updated!' }))
                .catch(() => setMsg({ type: 'danger', text: 'Could not save photo.' }));
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const clearPhoto = () => {
        localStorage.removeItem(photoKey(slug));
        setLocalPhoto('');
        api.patch(`/tenant/${slug}/askos/me`, { profile_photo: null }, { headers: askosAuthHeader() }).catch(() => {});
    };

    const saveProfile = async () => {
        setSaving(true); setMsg(null);
        try {
            await api.patch(`/tenant/${slug}/askos/me`, { full_name: fullName }, { headers: askosAuthHeader() });
            setMsg({ type: 'success', text: 'Profile updated.' });
        } catch (e) {
            setMsg({ type: 'danger', text: e.response?.data?.detail || 'Update failed.' });
        } finally {
            setSaving(false);
        }
    };

    if (!profile) {
        return <div className="subpage"><Spinner animation="border" size="sm" /></div>;
    }

    return (
        <div className="subpage">
            <h2>Settings</h2>
            {msg && <Alert variant={msg.type} className="py-2 small">{msg.text}</Alert>}

            <Tabs defaultActiveKey="profile" className="mb-4">
                <Tab eventKey="profile" title="Profile">
                    <SectionLabel>Profile Photo</SectionLabel>
                    <div className="settings-photo-row">
                        <div className="settings-avatar">
                            {localPhoto ? <img src={localPhoto} alt="Profile" /> : <span>{(profile.full_name || profile.email || '?')[0].toUpperCase()}</span>}
                        </div>
                        <div>
                            <label className="settings-upload-btn">
                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                                Upload Photo
                            </label>
                            {localPhoto && <button type="button" onClick={clearPhoto} className="settings-remove-photo-btn">Remove photo</button>}
                            <p className="empty-note mb-0">Max 300 KB</p>
                        </div>
                    </div>

                    <SectionLabel>Personal Info</SectionLabel>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-uppercase text-muted">Display Name</Form.Label>
                        <Form.Control value={fullName} onChange={e => setFullName(e.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-uppercase text-muted">Email</Form.Label>
                        <Form.Control value={profile.email || ''} readOnly disabled />
                    </Form.Group>
                    <Button size="sm" onClick={saveProfile} disabled={saving} className="primary-btn">
                        {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
                    </Button>
                </Tab>

                <Tab eventKey="language" title="Language & Voice">
                    <SectionLabel>Preferred Language</SectionLabel>
                    <p className="empty-note mb-3">Controls chat language and read-aloud voice language.</p>
                    <Row className="g-2 mb-4">
                        {LANGUAGES.map(lang => (
                            <Col xs={6} md={4} key={lang.code}>
                                <OptionCard selected={prefs.language === lang.code} onClick={() => onPrefsChange({ ...prefs, language: lang.code })}>
                                    <strong>{lang.label}</strong><span className="settings-native">{lang.native}</span>
                                </OptionCard>
                            </Col>
                        ))}
                    </Row>

                    <SectionLabel>Read Aloud</SectionLabel>
                    <ToggleRow
                        id="voice-toggle"
                        label="Read AskOS responses aloud"
                        description="Automatically reads each new AI response aloud using your browser's built-in text-to-speech. A speaker button on every response also lets you replay it anytime, regardless of this setting."
                        checked={prefs.voiceEnabled}
                        onChange={v => onPrefsChange({ ...prefs, voiceEnabled: v })}
                    />
                    {prefs.voiceEnabled && (
                        <div className="pt-3">
                            <VoiceModelSelector prefs={prefs} onChange={onPrefsChange} />
                            {!prefs.voiceName && (
                                <div className="mb-4">
                                    <p className="fw-semibold small mb-2">Voice Type</p>
                                    <PillGroup options={[{ v: 'female', l: 'Female' }, { v: 'male', l: 'Male' }]} value={prefs.voiceType || 'female'} getLabel={s => s.l} getValue={s => s.v} onChange={v => onPrefsChange({ ...prefs, voiceType: v })} />
                                </div>
                            )}
                            <div className="mb-3">
                                <p className="fw-semibold small mb-2">Speed</p>
                                <PillGroup options={VOICE_SPEEDS} value={prefs.voiceSpeed} getLabel={s => s.label} getValue={s => s.value} onChange={v => onPrefsChange({ ...prefs, voiceSpeed: v })} />
                            </div>
                            <Button size="sm" variant="outline-secondary"
                                onClick={() => speakText('Hello! I am AskOS, your AI professor. Voice settings are working correctly.', prefs.language, Number(prefs.voiceSpeed) || 1.0, prefs.voiceType || 'female', prefs.voiceName || null)}>
                                🔊 Play test
                            </Button>
                        </div>
                    )}
                </Tab>

                <Tab eventKey="accessibility" title="Accessibility">
                    <SectionLabel>Theme</SectionLabel>
                    {THEMES.map(t => (
                        <OptionCard key={t.key} selected={prefs.theme === t.key} onClick={() => onPrefsChange({ ...prefs, theme: t.key })}>
                            <span className="settings-theme-icon">{t.icon}</span>
                            <div><strong>{t.label}</strong><div className="settings-theme-desc">{t.desc}</div></div>
                        </OptionCard>
                    ))}

                    <SectionLabel>Accent Colour</SectionLabel>
                    <div className="settings-accent-row">
                        {ACCENT_PRESETS.map(a => (
                            <button
                                key={a.hex} type="button" title={a.label} aria-label={`Accent: ${a.label}`} aria-pressed={prefs.accent === a.hex}
                                onClick={() => onPrefsChange({ ...prefs, accent: a.hex })}
                                className={`settings-accent-swatch${prefs.accent === a.hex ? ' selected' : ''}`}
                                style={{ background: a.hex }}
                            />
                        ))}
                        <input type="color" value={prefs.accent} onChange={e => onPrefsChange({ ...prefs, accent: e.target.value })} title="Custom colour" className="settings-accent-custom" />
                    </div>

                    <SectionLabel>Text &amp; Reading</SectionLabel>
                    <div className="mb-3">
                        <p className="fw-semibold small mb-2">Font size</p>
                        <PillGroup options={FONT_SIZES} value={prefs.fontSize} getLabel={f => f.label} getValue={f => f.key} onChange={v => onPrefsChange({ ...prefs, fontSize: v })} />
                    </div>
                    <div className="mb-4">
                        <p className="fw-semibold small mb-2">Line spacing</p>
                        <PillGroup options={LINE_SPACINGS} value={prefs.lineSpacing} getLabel={l => l.label} getValue={l => l.key} onChange={v => onPrefsChange({ ...prefs, lineSpacing: v })} />
                    </div>

                    <SectionLabel>Vision &amp; Motion (WCAG)</SectionLabel>
                    <ToggleRow id="dyslexia" label="Dyslexia-friendly font" description="Uses OpenDyslexic — designed to reduce letter confusion." checked={prefs.dyslexiaFont} onChange={v => onPrefsChange({ ...prefs, dyslexiaFont: v })} />
                    <ToggleRow id="reduce-motion" label="Reduce motion" description="Disables animations and transitions. Meets WCAG 2.1 SC 2.3.3." checked={prefs.reduceMotion} onChange={v => onPrefsChange({ ...prefs, reduceMotion: v })} />
                    <ToggleRow id="focus-rings" label="Enhanced focus indicators" description="Large, high-visibility keyboard focus rings." checked={prefs.focusIndicators} onChange={v => onPrefsChange({ ...prefs, focusIndicators: v })} />
                </Tab>
            </Tabs>
        </div>
    );
};

export default SettingsPage;
